import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  captureInviteAttribution,
  canUseInviteFeature,
  claimPendingInviteAttribution,
  getPendingInviteAttribution,
  inviteAttributionConstants,
} from '../../api/inviteApi';
import { useAuth } from '../../contexts/AuthContext';


/**
 * 모든 공개 경로의 ?ref= 코드를 first-touch로 보관하고, 사용자 초기화가
 * 끝난 뒤 서버에 한 번만 귀속을 요청한다. 화면은 렌더링하지 않는다.
 */
const InviteAttributionBridge = () => {
  const location = useLocation();
  const { currentUser, userInitialization } = useAuth();
  const enabled = canUseInviteFeature(currentUser);
  const [pendingInvite, setPendingInvite] = useState({
    code: null,
    revision: 0,
  });
  const inFlightClaimsRef = useRef(new Map());

  // Navigate/인증 redirect의 useEffect보다 먼저 저장해 쿼리 유실을 막는다.
  useLayoutEffect(() => {
    if (!enabled) return;
    const pending = captureInviteAttribution(location.search, location.pathname);
    setPendingInvite((current) => ({
      code: pending?.code || null,
      revision: current.revision + 1,
    }));
  }, [enabled, location.pathname, location.search]);

  useEffect(() => {
    if (!enabled) return undefined;

    const refreshPending = (event) => {
      if (
        event?.type === 'storage'
        && event.key
        && event.key !== inviteAttributionConstants.PENDING_INVITE_KEY
      ) {
        return;
      }
      const pending = getPendingInviteAttribution();
      setPendingInvite((current) => ({
        code: pending?.code || null,
        revision: current.revision + 1,
      }));
    };

    window.addEventListener('online', refreshPending);
    window.addEventListener('storage', refreshPending);
    return () => {
      window.removeEventListener('online', refreshPending);
      window.removeEventListener('storage', refreshPending);
    };
  }, [enabled]);

  useEffect(() => {
    if (
      !enabled
      || !pendingInvite.code
      || !currentUser?.uid
      || userInitialization?.uid !== currentUser.uid
      || userInitialization?.status !== 'ready'
    ) {
      return undefined;
    }

    const attemptKey = `${currentUser.uid}:${pendingInvite.code}`;
    let cancelled = false;
    let claimTask = inFlightClaimsRef.current.get(attemptKey);
    if (!claimTask) {
      claimTask = (async () => {
        let lastResult = null;
        const delays = [0, 750, 2500];
        for (const delay of delays) {
          if (delay > 0) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
          lastResult = await claimPendingInviteAttribution(currentUser);
          if (lastResult?.terminal) return lastResult;
        }
        return lastResult;
      })();
      inFlightClaimsRef.current.set(attemptKey, claimTask);
      claimTask.finally(() => {
        if (inFlightClaimsRef.current.get(attemptKey) === claimTask) {
          inFlightClaimsRef.current.delete(attemptKey);
        }
      });
    }

    claimTask.then((result) => {
      if (!cancelled && result?.terminal) {
        if (
          result.success === true
          && result.rewards_enabled === true
          && Number(result.invitee_reward_days || 0) > 0
        ) {
          window.dispatchEvent(new CustomEvent('inviteRewardApplied', {
            detail: {
              days: Number(result.invitee_reward_days),
            },
          }));
        }
        setPendingInvite((current) => ({
          code: null,
          revision: current.revision + 1,
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    currentUser,
    enabled,
    pendingInvite.code,
    pendingInvite.revision,
    userInitialization,
  ]);

  return null;
};

export default InviteAttributionBridge;
